import { useSmartQuery, useSmartMutation } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useQuery } from "@tanstack/react-query";
import { getOperations, getResourceOperationChoices, addOperation, getOperationDetail, startOperation, confirmRetirement, markRetired, completeRepair, returnToInventory, getReplacementOptions, completeReplacement, getWorkOrderJobCards, getAllSops, getJobCardPriorities, raiseServiceOrder } from "./api";
import { PAGE_SIZE } from "@shared/utils/constants";
import { queryClient } from "@shared/api/queries/queryClient";

export const useOperations = (
  utilityId: string | number,
  tab: string,
  page: number = 1,
  limit: number = PAGE_SIZE,
  searchData?: string
) => {
  return useSmartQuery(
    ["mx", "operation", utilityId, tab, page, limit, searchData],
    () => getOperations(utilityId, tab, page, limit, searchData),
    {
      enabled: !!utilityId && !!tab,
    }
  );
};

export const useResourceOperationChoices = () => {
  return useSmartQuery(
    ["mx", "choices", "resource_operation"],
    () => getResourceOperationChoices()
  );
};

export const useAddOperation = () => {
  return useSmartMutation(
    (payload: any) => addOperation(payload),
    {
          onSuccess: () => {
           
             queryClient.refetchQueries({
              queryKey: ["mx", "operation"],
            });
          },
        }
  );
};

export const useOperationDetail = (
  operationId: string | number,
  utilityId: string | number,
  tab: string,
  page: number = 1,
  limit: number = 10
) => {
  return useSmartQuery(
    ["mx", "operation_detail", operationId, utilityId, tab, page, limit],
    () => getOperationDetail(operationId, utilityId, tab, page, limit),
    {
      enabled: !!utilityId && !!operationId && !!tab,
    }
  );
};

export const useStartOperation = () => {
  return useSmartMutation(
    (operationId: string | number) => startOperation(operationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
      },
    }
  );
};

export const useConfirmRetirement = () => {
  return useSmartMutation(
    ({ operationId, payload }: { operationId: string | number, payload: any }) => confirmRetirement(operationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
      },
    }
  );
};

export const useMarkRetired = () => {
  return useSmartMutation(
    (operationId: string | number) => markRetired(operationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
      },
    }
  );
};

export const useCompleteRepair = () => {
  return useSmartMutation(
    ({ operationId, payload }: { operationId: string | number, payload: any }) => completeRepair(operationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
      },
    }
  );
};

export const useReturnToInventory = () => {
  return useSmartMutation(
    ({ operationId, payload }: { operationId: string | number, payload: any }) => returnToInventory(operationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
      },
    }
  );
};

export const useReplacementOptions = (operationId?: string | number) => {
  return useSmartQuery(
    ["mx", "operation", operationId, "replacement-options"],
    () => getReplacementOptions(operationId!),
    {
      enabled: !!operationId,
    }
  );
};

export const useCompleteReplacement = () => {
  return useSmartMutation(
    ({ operationId, payload }: { operationId: string | number, payload: FormData }) => completeReplacement(operationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
      },
    }
  );
};

export const useJobCardPriority = () => {
  return useSmartQuery(["mx", "choices", "jobcardPriorityOptions"], () =>
    getJobCardPriorities()
  );
};

export const useAllSops = (remoteUtilityId: string | number) => {
  return useSmartQuery(["mx", "sops", remoteUtilityId], () =>
    getAllSops(remoteUtilityId),
    {
      enabled: !!remoteUtilityId
    }
  );
};

export const useRaiseServiceOrder = () => {
  return useSmartMutation(
    ({ operationId, payload }: { operationId: string | number, payload: any }) => raiseServiceOrder(operationId, payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["mx", "operation"] });
        queryClient.invalidateQueries({ queryKey: ["mx", "operation_detail"] });
        queryClient.refetchQueries({
          queryKey: ["mx", "work-order-job-cards"],
        });
      },
    }
  );
};

export const useWorkOrderJobCards = (remoteUtilityId: string | number, operationId: string | number, page: number = 1, limit: number = 50) => {
  return useSmartQuery(
    ["mx", "work-order-job-cards", remoteUtilityId, operationId, page, limit],
    () => getWorkOrderJobCards(remoteUtilityId, operationId, page, limit),
    {
      enabled: !!remoteUtilityId && !!operationId
    }
  );
};
